import { BarChart3, TrendingUp, Users, Eye, Calendar } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';

export const AnalyticsModal = () => {
  const { session } = useAppStore();

  const analyticsData = [
    {
      title: 'Views',
      value: session?.hy_view || 0,
      change: '+12%',
      icon: Eye,
      color: 'text-brand',
      bgColor: 'bg-brand/10'
    },
    {
      title: 'Users',
      value: session?.hy_user_count || 0,
      change: '+8%',
      icon: Users,
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      title: 'Sessions',
      value: 24,
      change: '+15%',
      icon: BarChart3,
      color: 'text-status-warning',
      bgColor: 'bg-status-warning/10'
    },
    {
      title: 'Engagement',
      value: '89%',
      change: '+3%',
      icon: TrendingUp,
      color: 'text-status-success',
      bgColor: 'bg-status-success/10'
    }
  ];

  const chartData = Array.from({ length: 7 }, (_, i) => ({
    day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
    views: Math.floor(Math.random() * 100) + 50,
    users: Math.floor(Math.random() * 30) + 10
  }));

  return (
    <div className="p-6 space-y-6">
      {/* Analytics Header */}
      <div className="card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <BarChart3 className="w-6 h-6 text-brand" />
          <h3 className="text-xl font-semibold text-th-primary">
            Analytics Dashboard
          </h3>
        </div>
        <p className="text-th-secondary">
          Track your app performance and user engagement.
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {analyticsData.map((metric, _) => (
          <div key={metric.title} className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className={`p-2 rounded-lg ${metric.bgColor}`}>
                <metric.icon className={`w-5 h-5 ${metric.color}`} />
              </div>
              <span className="text-status-success text-sm font-medium">
                {metric.change}
              </span>
            </div>
            <p className="text-th-secondary text-sm mb-1">
              {metric.title}
            </p>
            <p className="text-2xl font-bold text-th-primary">
              {metric.value}
            </p>
          </div>
        ))}
      </div>

      {/* Chart Visualization */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-lg font-semibold text-th-primary">
            Weekly Overview
          </h4>
          <div className="flex items-center space-x-2 text-th-secondary">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">Last 7 days</span>
          </div>
        </div>

        {/* Simple Bar Chart */}
        <div className="space-y-4">
          {chartData.map((day, _) => (
            <div key={day.day} className="flex items-center space-x-4">
              <span className="text-th-secondary text-sm w-8">
                {day.day}
              </span>
              <div className="flex-1 space-y-2">
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-surface rounded-full h-2">
                    <div
                      className="bg-brand h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(day.views / 150) * 100}%` }}
                    />
                  </div>
                  <span className="text-th-secondary text-xs w-8">
                    {day.views}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-surface rounded-full h-2">
                    <div
                      className="bg-accent h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(day.users / 40) * 100}%` }}
                    />
                  </div>
                  <span className="text-th-secondary text-xs w-8">
                    {day.users}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center space-x-6 mt-6 pt-4 border-t border-subtle">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-brand rounded-full"></div>
            <span className="text-th-secondary text-sm">Views</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-accent rounded-full"></div>
            <span className="text-th-secondary text-sm">Users</span>
          </div>
        </div>
      </div>

      {/* Performance Summary */}
      <div className="bg-elevated border border-subtle rounded-xl p-6">
        <h4 className="text-lg font-semibold text-th-primary mb-4">
          Performance Summary
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-th-secondary">Average Session Duration</span>
              <span className="text-th-primary font-medium">4m 32s</span>
            </div>
            <div className="flex justify-between">
              <span className="text-th-secondary">Bounce Rate</span>
              <span className="text-th-primary font-medium">23%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-th-secondary">Return Visitors</span>
              <span className="text-th-primary font-medium">67%</span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-th-secondary">Peak Hours</span>
              <span className="text-th-primary font-medium">2PM - 4PM</span>
            </div>
            <div className="flex justify-between">
              <span className="text-th-secondary">Top Location</span>
              <span className="text-th-primary font-medium">{session?.hy_country}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-th-secondary">Conversion Rate</span>
              <span className="text-th-primary font-medium">12.4%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
